import { useEffect, useState } from "react";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { PERIODICIDAD_FACTORAJE } from "@/lib/factoraje";

/** Mesa de Control · Factoraje: días y descuento recalculados con la fecha de disposición elegida. */
export function FactorajeDesembolsoPanel({ creditoId, factoraje, fecha, onPreview }) {
  const [prev, setPrev] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!creditoId || !fecha) return undefined;
    let vivo = true;
    const t = setTimeout(async () => {
      setCargando(true);
      try {
        const { data } = await api.post(`/desembolso/${creditoId}/vista-previa-factoraje`, { fecha_desembolso: fecha });
        if (vivo) { setPrev(data); setError(""); onPreview?.(data); }
      } catch (e) {
        if (vivo) { setPrev(null); setError(apiError(e)); onPreview?.(null); }
      } finally {
        if (vivo) setCargando(false);
      }
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [creditoId, fecha]); // eslint-disable-line react-hooks/exhaustive-deps

  const filas = [
    ["Vencimiento de la factura", fdate(factoraje?.fecha_vencimiento_factura), "mc-fx-vencimiento"],
    ["Fecha de disposición", fdate(fecha), "mc-fx-disposicion"],
    ["Fecha de pago", prev ? fdate(prev.fecha_pago) : "—", "mc-fx-fecha-pago"],
    ["Días de descuento", prev?.dias_descuento ?? "—", "mc-fx-dias"],
    ["Descuento (interés)", prev ? mxn(prev.descuento) : "—", "mc-fx-descuento"],
    ["IVA del descuento", prev ? mxn(prev.iva_descuento) : "—", "mc-fx-iva"],
    ["Total al vencimiento", prev ? mxn(prev.total_vencimiento) : "—", "mc-fx-total"],
    ["CAT", prev?.cat != null ? `${prev.cat}%` : "—", "mc-fx-cat"],
  ];
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3" data-testid="mc-factoraje-panel">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-indigo-900">
        <FileText className="h-3.5 w-3.5" /> Factoraje · {PERIODICIDAD_FACTORAJE}
        {cargando && <Loader2 className="h-3 w-3 animate-spin" />}
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {filas.map(([l, v, tid]) => (
          <div key={tid} className="flex justify-between gap-2">
            <dt className="text-slate-500">{l}</dt>
            <dd className="font-mono tabular-nums text-slate-800" data-testid={tid}>{v}</dd>
          </div>
        ))}
      </dl>
      {error && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-rose-700" data-testid="mc-fx-error">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
        </p>
      )}
      <p className="mt-2 text-[11px] text-slate-500">
        Los días y el descuento se recalculan con la fecha real de disposición; la fecha debe ser anterior al
        vencimiento de la factura.
      </p>
    </div>
  );
}
